"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";

/**
 * Reusable Modal Component
 *
 * A fixed-position modal with consistent styling, typography, and spacing.
 * Follows DRY principles and is fully responsive.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal should close
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.className - Additional CSS classes for the modal container
 * @param {Object} props.style - Additional inline styles for the modal container
 * @param {boolean} props.showCloseButton - Whether to show the close button (default: true)
 * @param {string} props.closeButtonClassName - Additional CSS classes for close button
 */
export default function Modal(t0) {
  const $ = _c(28);
  const {
    open,
    onClose,
    children,
    className: t1,
    style: t2,
    showCloseButton: t3,
    closeButtonClassName: t4
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const style = t5;
  const showCloseButton = t3 === undefined ? true : t3;
  const closeButtonClassName = t4 === undefined ? "" : t4;
  const modalRef = useRef(null);
  let t6;
  let t7;
  if ($[2] !== open) {
    t6 = () => {
      if (!open) {
        document.body.style.overflow = "";
        return;
      }
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      if (modalRef.current) {
        modalRef.current.focus();
      }
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    };
    t7 = [open];
    $[2] = open;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[5] !== onClose || $[6] !== open) {
    t8 = () => {
      if (!open) {
        return;
      }
      const handleEscape = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    };
    t9 = [open, onClose];
    $[5] = onClose;
    $[6] = open;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  useEffect(t8, t9);
  if (!open) {
    return null;
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      backgroundColor: "rgba(0, 0, 0, 0.5)"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== onClose) {
    t11 = <div className="fixed inset-0 z-[60]" style={t10} onClick={onClose} aria-hidden="true" />;
    $[10] = onClose;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const t12 = `fixed z-[70] flex flex-col w-[calc(100vw-48px)] max-w-[364px] h-[calc(100vh-100px)] max-h-[556px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:w-[364px] md:h-[556px] ${className}`;
  let t13;
  if ($[12] !== style) {
    t13 = {
      padding: "var(--spacing16)",
      gap: "var(--spacing24)",
      borderRadius: "var(--radius24)",
      border: "1px solid var(--colorDisabledOnColorDisabled)",
      backgroundColor: "var(--colorSecondaryOnSecondary)",
      boxShadow: "0px 4px 4px 0px #0000004D, 0px 8px 12px 6px #00000026",
      outline: "none",
      ...style
    };
    $[12] = style;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== closeButtonClassName || $[15] !== onClose || $[16] !== showCloseButton) {
    t14 = showCloseButton && <div className="absolute top-4 right-4 z-10"><IconButton onClick={onClose} className={closeButtonClassName} sx={{
        padding: "4px",
        color: "var(--colorPrimaryPrimary, #004BEE)",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)"
        }
      }} aria-label="Close modal"><Close /></IconButton></div>;
    $[14] = closeButtonClassName;
    $[15] = onClose;
    $[16] = showCloseButton;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== children) {
    t15 = <div className="flex-1 min-h-0 flex flex-col">{children}</div>;
    $[18] = children;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t12 || $[21] !== t13 || $[22] !== t14 || $[23] !== t15) {
    t16 = <div ref={modalRef} className={t12} style={t13} role="dialog" aria-modal="true" tabIndex={-1} onClick={_temp}>{t14}{t15}</div>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t11 || $[26] !== t16) {
    t17 = <>{t11}{t16}</>;
    $[25] = t11;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  return t17;
}
function _temp(e) {
  return e.stopPropagation();
}